import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Company } from "../../utils/api/endpoints/companies";
import {
  MdArrowBack,
  MdBusiness,
  MdPeople,
  MdSecurity,
  MdTimeline,
  MdBugReport,
  MdWarning,
  MdError,
  MdDescription,
  MdCalendarToday,
  MdAdd,
  MdClose,
  MdWebAsset,
  MdLock,
  MdRefresh,
  MdInfo,
  MdPlayArrow,
  MdCheckCircle,
  MdUndo,
  MdHelp,
  MdLightbulb,
  MdLayers,
  MdCancel,
  MdStar,
  MdVpnKey
} from "react-icons/md";
import AssetInventoryPage from "../Assets/AssetInventoryPage";
import axios from "../../utils/api/axios";
import { CTBProgram, parseProgramDetails, ProgramAPIResponse } from "../../utils/api/endpoints/programs/parsePrograms";
import ProgramCard from "../../components/programs/cards/ProgramCard";
import ProgramQuickView from "../../components/programs/ProgramQuickView";
import { isProgramDraft } from "../../utils/programs/statusUtils";
import moment from "moment";
import { Calendar, momentLocalizer, Event } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { formatRole, ROLE_OPTIONS } from "../../utils/hooks/multi-tenant/invitation";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { createInvitationOnBehalf } from "../../utils/api/endpoints/user/invitation";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import toast from "react-hot-toast";
import {
  getVaultsByCompany,
  linkVaultToProgram,
  unlinkVaultFromProgram,
  getLinkedProgramsForVault,
  LinkedProgramInfo,
  LinkVaultOptions
} from "../../utils/api/endpoints/vault";
import AccessPasswordModal from "../../components/vault/AccessPasswordModal";
import CompanySubscriptionTab from "../../components/companies/CompanySubscriptionTab";
import AdminCompanySSOTab from "../../components/admin/AdminCompanySSOTab";
import ProgramFeedbackManagement from "../admin/ProgramFeedbackManagement";
import { ScopedReportModal } from "../../components/common/ScopedReportModal";
import { FiLink, FiX, FiShield } from "react-icons/fi";

interface CompanyDashboardProps {
  company: Company;
  onBack: () => void;
}

interface VulnerabilityStats {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  total: number;
}

const CompanyDashboard: React.FC<CompanyDashboardProps> = ({
  company,
  onBack
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role: currentUserRole, id: currentUserId } = useUserCredentials();
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [programs, setPrograms] = useState<CTBProgram[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState<CTBProgram>();
  const [pentestReportsCount, setPentestReportsCount] = useState<number>(0);
  const [pentestReports, setPentestReports] = useState<any[]>([]);
  const [allRuns, setAllRuns] = useState<any[]>([]);
  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);
  const [actionTab, setActionTab] = useState<"for-me" | "by-company">("for-me");

  // Pentest reports filters
  const [pentestSearch, setPentestSearch] = useState<string>("");
  const [pentestStatusFilter, setPentestStatusFilter] = useState<string>("all");

  // Program filters
  const [programSearch, setProgramSearch] = useState<string>("");
  const [programTypeFilter, setProgramTypeFilter] = useState<string>("all");
  const [programStatusFilter, setProgramStatusFilter] = useState<string>("all");
  const [programTestingTypeFilter, setProgramTestingTypeFilter] = useState<string>("all");
  const [programSortBy, setProgramSortBy] = useState<string>("date_desc");

  // User filters
  const [userSearch, setUserSearch] = useState<string>("");
  const [userRoleFilter, setUserRoleFilter] = useState<string>("all");
  const [userTypeFilter, setUserTypeFilter] = useState<string>("all");
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<number | null>(null);

  // Invitation form state
  const [showInvitationForm, setShowInvitationForm] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState("");
  const [invitationRole, setInvitationRole] = useState<UserRole>(UserRole.DEVELOPER);
  const [selectedBusinessOwner, setSelectedBusinessOwner] = useState<number | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);

  // Runs and Quota state
  const [companyRuns, setCompanyRuns] = useState<any[]>([]);
  const [companyQuota, setCompanyQuota] = useState<any>(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const [forcingComplete, setForcingComplete] = useState(false);
  const [undoingComplete, setUndoingComplete] = useState(false);

  // Reports state and filters
  const [reports, setReports] = useState<any[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportSearch, setReportSearch] = useState<string>("");
  const [reportSeverityFilter, setReportSeverityFilter] = useState<string>("all");
  const [reportStatusFilter, setReportStatusFilter] = useState<string>("all");
  const [reportProgramFilter, setReportProgramFilter] = useState<string>("all");

  // Activity logs state and filters
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>("all");
  const [activityUserFilter, setActivityUserFilter] = useState<string>("all");

  // Calendar view state
  const [calendarView, setCalendarView] = useState<"month" | "quarter" | "year">("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [researchersMap, setResearchersMap] = useState<Record<number, { username: string; email: string }>>({});

  const [vulnerabilityStats, setVulnerabilityStats] = useState<VulnerabilityStats>({
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
    total: 0
  });

  // Vault state
  const [vaults, setVaults] = useState<any[]>([]);
  const [vaultsLoading, setVaultsLoading] = useState(false);
  const [vaultSearch, setVaultSearch] = useState("");
  const [linkedProgramsMap, setLinkedProgramsMap] = useState<Record<number, LinkedProgramInfo[]>>({});
  const [showAccessPasswordModal, setShowAccessPasswordModal] = useState(false);
  const [showScopedReportModal, setShowScopedReportModal] = useState(false);
  const [selectedLinkVault, setSelectedLinkVault] = useState<any>(null);
  const [selectedLinkProgram, setSelectedLinkProgram] = useState<CTBProgram | undefined>(undefined);
  const [showLinkProgramModal, setShowLinkProgramModal] = useState(false);

  // Get business owners (parent users)
  const businessOwners = company.users?.filter(
    u => u.role === 2 && !u.parent_user_id
  ) || [];

  // Get invited users (sub-users)
  const invitedUsers = company.users?.filter(
    u => u.parent_user_id
  ) || [];

  // Check if current user is admin
  const isAdmin = currentUserRole === UserRole.ADMIN;
  const isAuthorizedToManageRoles = currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SUB_ADMIN;

  // Persist active company to sessionStorage for program creation & flows
  useEffect(() => {
    if (company?.company_id) {
      sessionStorage.setItem("ctb_admin_active_company_id", String(company.company_id));
      if (company.company_name) {
        sessionStorage.setItem("ctb_admin_active_company_name", company.company_name);
      }
    }
  }, [company?.company_id, company?.company_name]);

  // Handle invitation creation on behalf of business owner
  const handleCreateInvitation = async () => {
    if (!invitationEmail || !selectedBusinessOwner) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(invitationEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setInvitationLoading(true);
    const toastId = toast.loading("Sending invitation...");

    try {
      await createInvitationOnBehalf({
        email: invitationEmail,
        role: invitationRole,
        business_owner_id: selectedBusinessOwner
      });

      toast.success("Invitation sent successfully on behalf of business owner", { id: toastId });

      // Reset form
      setInvitationEmail("");
      setInvitationRole(UserRole.DEVELOPER);
      setSelectedBusinessOwner(null);
      setShowInvitationForm(false);

      // Optionally refresh the page or company data
      window.location.reload();
    } catch (error: any) {
      console.error("Failed to send invitation:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to send invitation";
      toast.error(errorMessage, { id: toastId });
    } finally {
      setInvitationLoading(false);
    }
  };

  // Handle role update
  const handleUpdateRole = async (userId: number, newRole: UserRole) => {
    setUpdatingRoleUserId(userId);
    const toastId = toast.loading("Updating user role...");

    try {
      await axios.post("/v2/user/role", {
        user_id: userId,
        role: newRole
      });

      toast.success("User role updated successfully", { id: toastId });

      // Refresh local state or company object
      window.location.reload();
    } catch (error: any) {
      console.error("Failed to update user role:", error);
      const errorMessage = error?.response?.data?.message || "Failed to update user role";
      toast.error(errorMessage, { id: toastId });
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  // Handle Force Complete
  const handleForceComplete = async () => {
    if (!window.confirm("Are you sure you want to force complete the current comprehensive cycle? This will reset the target coverage tracking and consume 1 quota unit.")) {
      return;
    }

    setForcingComplete(true);
    const toastId = toast.loading("Forcing cycle completion...");

    try {
      await axios.post(`/v2/program-runs/company/${company.company_id}/force-complete`, {
        reason: "Administrative override from Company Dashboard"
      });

      toast.success("Comprehensive cycle forced to completion", { id: toastId });
      
      // Refresh data
      fetchCompanyRunsAndQuota();
    } catch (error: any) {
      console.error("Failed to force complete cycle:", error);
      const errorMessage = error?.response?.data?.message || "Failed to force complete cycle";
      toast.error(errorMessage, { id: toastId });
    } finally {
      setForcingComplete(false);
    }
  };

  // Handle Undo Force Complete
  const handleUndoForceComplete = async () => {
    if (!window.confirm("Are you sure you want to undo the latest administrative override? This will restore the previous coverage tracking state for the company.")) {
      return;
    }

    setUndoingComplete(true);
    const toastId = toast.loading("Undoing administrative override...");

    try {
      await axios.delete(`/v2/program-runs/company/${company.company_id}/force-complete`);
      toast.success("Latest administrative override successfully removed.", { id: toastId });
      
      // Refresh data
      fetchCompanyRunsAndQuota();
    } catch (error: any) {
      console.error("Failed to undo override:", error);
      const errorMessage = error?.response?.data?.message || "Failed to undo override";
      toast.error(errorMessage, { id: toastId });
    } finally {
      setUndoingComplete(false);
    }
  };

  // Fetch runs (quota is lazy-loaded when the Runs tab is opened)
  const fetchCompanyRunsAndQuota = async (includeQuota: boolean = false) => {
    setRunsLoading(true);
    try {
      const [runsRes, quotaRes] = await Promise.all([
        axios.get("/v2/program-runs", { params: { company_id: company.company_id } }),
        includeQuota || activeTab === "runs"
          ? axios.get("/v2/program-runs/quota", { params: { company_id: company.company_id } })
          : Promise.resolve({ data: null })
      ]);
      setCompanyRuns(runsRes.data.data || []);
      if (quotaRes?.data) {
        setCompanyQuota(quotaRes.data.success ? quotaRes.data.data : quotaRes.data);
      }
    } catch (error) {
      console.error("Error fetching company runs and quota:", error);
    } finally {
      setRunsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyRunsAndQuota(false);
  }, [company.company_id]);

  // Fetch quota lazily when user navigates to the runs tab
  useEffect(() => {
    if (activeTab === "runs" && !companyQuota) {
      axios
        .get("/v2/program-runs/quota", { params: { company_id: company.company_id } })
        .then(res => setCompanyQuota(res.data.success ? res.data.data : res.data))
        .catch(err => console.error("Error fetching quota:", err));
    }
  }, [activeTab, company.company_id, companyQuota]);

  // Handle query parameter and hash-based tab navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabQuery = searchParams.get("tab");
    const hash = location.hash.replace('#', '');
    const targetTab = tabQuery || hash;
    const validTabs = ['overview', 'subscription', 'sso', 'programs', 'assets', 'users', 'reports', 'pentest-reports', 'vault', 'activity', 'calendar', 'runs', 'feedbacks'];

    if (targetTab && validTabs.includes(targetTab)) {
      setActiveTab(targetTab);
      // Clear the hash from URL after setting the tab to keep URL clean, while preserving search parameters
      if (hash && !tabQuery) {
        setTimeout(() => {
          window.history.replaceState(null, '', `${location.pathname}${location.search}`);
        }, 100);
      }
    }
  }, [location.search, location.hash, location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      if (!company.users || company.users.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const userIds = company.users.map(u => u.user_id);
        const parentUserIds = company.users
          ?.filter(user => user.parent_user_id === null && user.role === 2)
          .map(user => user.user_id) || [];

        // Run all independent queries in parallel for instant loading
        const [programsResponse, pentestResponse, assetsResponse, scopingResponse] = await Promise.all([
          axios.get("/v2/programs", {
            params: { company_id: company.company_id, limit: 1000, page: 0 }
          }).catch(err => {
            console.error("Error fetching programs:", err);
            return { data: { programs: [] } };
          }),
          parentUserIds.length > 0
            ? axios.get("/v2/automated-report/admin/pentest-reports", {
                params: {
                  business_ids: parentUserIds.join(","),
                  limit: 1000,
                  page: 1
                }
              }).catch(err => {
                console.error("Error fetching pentest reports:", err);
                return { data: null };
              })
            : Promise.resolve({ data: null }),
          axios.get("/assets", {
            params: { company_id: company.company_id }
          }).catch(err => {
            console.error("Error fetching assets:", err);
            return { data: [] };
          }),
          axios.get("/v2/automated-report/scoping-options", {
            params: { company_id: company.company_id }
          }).catch(err => {
            console.error("Error fetching scoping runs for dashboard:", err);
            return { data: null };
          })
        ]);

        // Process pentest reports
        if (pentestResponse.data?.data?.reports) {
          const reports = pentestResponse.data.data.reports;
          setPentestReportsCount(reports.length);
          setPentestReports(reports);
        } else {
          setPentestReportsCount(0);
          setPentestReports([]);
        }

        // Process assets
        if (assetsResponse.data) {
          setAssets(assetsResponse.data);
        }

        // Process scoping runs
        if (scopingResponse.data?.status === "success") {
          setAllRuns(scopingResponse.data.data?.runs || []);
        }

        // Process programs
        let parsedPrograms: CTBProgram[] = [];
        if (programsResponse.data?.programs) {
          const rawPrograms = programsResponse.data.programs.filter(
            (p: ProgramAPIResponse) => {
              if (!p || !p.program_title) return false;
              const pCompanyId = Number(p.company_id || p.asset?.company_id);
              if (pCompanyId) {
                return pCompanyId === company.company_id;
              }
              return userIds.includes(p.user_id);
            }
          );

          parsedPrograms = rawPrograms
            .map((p: ProgramAPIResponse) => {
              try {
                return parseProgramDetails(p);
              } catch (error) {
                console.error("Error parsing program:", error);
                return null;
              }
            })
            .filter((p: CTBProgram | null): p is CTBProgram => p !== null);

          setPrograms(parsedPrograms);
        }

        // Fetch reports ONCE to populate both vulnerability stats and the reports list
        const visibleProgramIds = parsedPrograms
          .map((p: CTBProgram) => p.id)
          .join(",");

        if (visibleProgramIds) {
          try {
            const reportsResponse = await axios.get("/v2/reports", {
              params: {
                program: visibleProgramIds,
                pageCount: 5000,
                page: 1,
                includeArchived: "true",
                includeClosed: "true"
              }
            });

            if (reportsResponse.data?.data && Array.isArray(reportsResponse.data.data)) {
              const fetchedReports = reportsResponse.data.data;
              setReports(fetchedReports);

              const apiTotal = reportsResponse.data.results ?? fetchedReports.length;
              const stats: VulnerabilityStats = {
                critical: 0,
                high: 0,
                medium: 0,
                low: 0,
                info: 0,
                total: apiTotal
              };

              fetchedReports.forEach((report: any) => {
                const severity = report.severity_category?.toLowerCase();
                if (severity === "critical") stats.critical++;
                else if (severity === "high") stats.high++;
                else if (severity === "medium") stats.medium++;
                else if (severity === "low") stats.low++;
                else if (severity === "info" || severity === "informational") stats.info++;
              });

              setVulnerabilityStats(stats);
            }
          } catch (error) {
            console.error("Error fetching reports for company dashboard:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company?.company_id]);

  // Fetch reports when switching to reports tab only if not already loaded
  useEffect(() => {
    const fetchReports = async () => {
      if (programs.length === 0 || activeTab !== "reports" || reports.length > 0) {
        return;
      }

      setReportsLoading(true);
      try {
        const programIds = programs.map(p => p.id).join(",");
        const response = await axios.get("/v2/reports", {
          params: {
            program: programIds,
            pageCount: 5000,
            page: 1,
            includeArchived: "true",
            includeClosed: "true"
          }
        });

        if (response.data?.data && Array.isArray(response.data.data)) {
          setReports(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching reports on tab switch:", error);
      } finally {
        setReportsLoading(false);
      }
    };

    fetchReports();
  }, [activeTab]);

  // Fetch activity logs when Activity tab is active
  useEffect(() => {
    const fetchActivityLogs = async () => {
      if (activeTab === "activity") {
        setActivityLoading(true);
        try {
          console.log("Fetching activity logs...");

          const response = await axios.get("/v2/rbac/getRbacLogs");
          console.log("Activity logs response:", response.data);

          if (Array.isArray(response.data)) {
            // Filter logs to only show activities from company users
            const companyUserIds = (company.users || []).map(u => u.user_id);
            const companyLogs = response.data.filter((log: any) =>
              companyUserIds.includes(log.user_id)
            );
            console.log("Filtered company logs:", companyLogs.length);
            setActivityLogs(companyLogs);
          } else {
            console.log("Invalid activity logs response");
            setActivityLogs([]);
          }
        } catch (error: any) {
          console.error("Error fetching activity logs:", error);
          if (error?.response) {
            console.error("Error response:", error.response.data);
          }
          setActivityLogs([]);
        } finally {
          setActivityLoading(false);
        }
      }
    };

    fetchActivityLogs();
  }, [activeTab, company.users]);

  // Fetch researcher details when Calendar tab is active
  useEffect(() => {
    const fetchResearchers = async () => {
      if (activeTab === "calendar") {
        // Collect all unique researcher IDs from all programs
        const researcherIds = new Set<number>();
        programs.forEach(program => {
          if (program.privateAccessUsers && Array.isArray(program.privateAccessUsers)) {
            program.privateAccessUsers.forEach(id => researcherIds.add(id));
          }
        });

        console.log("Researcher IDs to fetch:", Array.from(researcherIds));

        if (researcherIds.size > 0) {
          const idsArray = Array.from(researcherIds);
          const map: Record<number, { username: string; email: string }> = {};

          // Fetch each researcher individually
          try {
            const fetchPromises = idsArray.map(async (userId) => {
              try {
                const response = await axios.get(`/v2/user?id=${userId}`);
                console.log(`Fetched user ${userId}:`, response.data);

                if (response.data?.user) {
                  const user = response.data.user;
                  map[userId] = {
                    username: user.username || user.display_name || `User ${userId}`,
                    email: user.email || ""
                  };
                }
              } catch (err) {
                console.error(`Error fetching user ${userId}:`, err);
              }
            });

            await Promise.all(fetchPromises);
            console.log("Final researchers map:", map);
            setResearchersMap(map);
          } catch (error: any) {
            console.error("Error fetching researchers:", error);
          }
        } else {
          console.log("No researcher IDs found in programs");
        }
      }
    };

    fetchResearchers();
  }, [activeTab, programs]);

  const refreshVaults = async (fetchLinked: boolean = false) => {
    setVaultsLoading(true);
    try {
      const response = await getVaultsByCompany(company.company_id);
      const fetchedVaults = response.vaults || [];
      setVaults(fetchedVaults);
      
      // Only fetch linked programs when on the vault tab or explicitly requested
      if (fetchLinked || activeTab === "vault") {
        const linkedMap: Record<number, LinkedProgramInfo[]> = {};
        await Promise.all(fetchedVaults.map(async (v: any) => {
          try {
            const { linkedPrograms } = await getLinkedProgramsForVault(v.vault_id);
            linkedMap[v.vault_id] = linkedPrograms || [];
          } catch (e) {
            console.error(`Error fetching links for vault ${v.vault_id}:`, e);
          }
        }));
        setLinkedProgramsMap(linkedMap);
      }
    } catch (error) {
      console.error("Error fetching vaults:", error);
    } finally {
      setVaultsLoading(false);
    }
  };

  // Fetch vaults on mount (fetches only the vault list to populate the tab count badge)
  useEffect(() => {
    refreshVaults(false);
  }, [company.company_id]);

  // Fetch linked programs for vaults only when Vault tab is opened
  useEffect(() => {
    if (activeTab === "vault" && vaults.length > 0 && Object.keys(linkedProgramsMap).length === 0) {
      refreshVaults(true);
    }
  }, [activeTab, vaults.length, linkedProgramsMap]);

  const handleAccessPasswordSubmit = async (options: LinkVaultOptions) => {
    if (!selectedLinkVault || !selectedLinkProgram?.id) return;
    try {
      await linkVaultToProgram(selectedLinkVault.vault_id, selectedLinkProgram.id, options);
      setShowAccessPasswordModal(false);
      setSelectedLinkProgram(undefined);
      toast.success("Vault successfully linked to program!");
      refreshVaults();
      setShowLinkProgramModal(true);
    } catch (error) {
      console.error("Error linking vault:", error);
      throw error;
    }
  };

  const handleUnlinkVault = async (vaultId: number, programId: number) => {
    if (!window.confirm("Are you sure you want to unlink this vault from the program?")) return;
    const toastId = toast.loading("Unlinking vault...");
    try {
      await unlinkVaultFromProgram(vaultId, programId);
      toast.success("Vault successfully unlinked!", { id: toastId });
      refreshVaults();
    } catch (error) {
      console.error("Error unlinking vault:", error);
      toast.error("Failed to unlink vault", { id: toastId });
    }
  };

  // Filter programs with useMemo to avoid re-filtering on incidental renders
  const filteredPrograms = useMemo(() => {
    return programs.filter(program => {
      // Search filter
      if (programSearch && !program.title?.toLowerCase().includes(programSearch.toLowerCase())) {
        return false;
      }

      // Type filter
      if (programTypeFilter !== "all" && program.type !== programTypeFilter) {
        return false;
      }

      // Status filter
      if (programStatusFilter === "active" && !program.isActivated) {
        return false;
      }
      if (programStatusFilter === "inactive" && program.isActivated) {
        return false;
      }

      // Testing type filter
      if (programTestingTypeFilter !== "all" && program.testing_type !== programTestingTypeFilter) {
        return false;
      }

      return true;
    });
  }, [programs, programSearch, programTypeFilter, programStatusFilter, programTestingTypeFilter]);

  // Sort programs based on selected sort option
  const sortedPrograms = useMemo(() => {
    return [...filteredPrograms].sort((a, b) => {
      switch (programSortBy) {
        case "date_desc":
          return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
        case "date_asc":
          return moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf();
        case "name_asc":
          return (a.title || "").localeCompare(b.title || "");
        case "name_desc":
          return (b.title || "").localeCompare(a.title || "");
        case "status":
          return (b.isActivated ? 1 : 0) - (a.isActivated ? 1 : 0);
        default:
          return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
      }
    });
  }, [filteredPrograms, programSortBy]);

  // Get active and inactive program counts from VISIBLE programs
  const activeProgramsCount = filteredPrograms.filter(p => p.isActivated).length;
  const inactiveProgramsCount = filteredPrograms.filter(p => !p.isActivated).length;

  // Filter users
  const filteredUsers = (company.users || []).filter(user => {
    // Search filter
    if (userSearch) {
      const searchLower = userSearch.toLowerCase();
      const matchesUsername = user.username?.toLowerCase().includes(searchLower);
      const matchesEmail = user.email?.toLowerCase().includes(searchLower);
      if (!matchesUsername && !matchesEmail) {
        return false;
      }
    }

    // Role filter
    if (userRoleFilter !== "all" && user.role.toString() !== userRoleFilter) {
      return false;
    }

    // Type filter (owner vs sub-user)
    if (userTypeFilter === "owner" && user.parent_user_id !== null) {
      return false;
    }
    if (userTypeFilter === "subuser" && user.parent_user_id === null) {
      return false;
    }

    return true;
  });

  // Filter reports
  const filteredReports = reports.filter(report => {
    // Search filter
    if (reportSearch) {
      const searchLower = reportSearch.toLowerCase();
      const matchesTitle = report.report_title?.toLowerCase().includes(searchLower);
      const matchesId = report.report_id?.toString().includes(searchLower);
      if (!matchesTitle && !matchesId) {
        return false;
      }
    }

    // Severity filter
    if (reportSeverityFilter !== "all" && report.severity_category !== reportSeverityFilter) {
      return false;
    }

    // Status filter
    if (reportStatusFilter !== "all" && report.state !== reportStatusFilter) {
      return false;
    }

    // Program filter
    if (reportProgramFilter !== "all" && report.program_id?.toString() !== reportProgramFilter) {
      return false;
    }

    return true;
  });

  // Filter activity logs
  const filteredActivityLogs = activityLogs.filter(log => {
    // Module/Type filter
    if (activityTypeFilter !== "all" && log.module !== activityTypeFilter) {
      return false;
    }

    // User filter
    if (activityUserFilter !== "all" && log.user_id?.toString() !== activityUserFilter) {
      return false;
    }

    return true;
  });

  // Get unique modules for filter
  const uniqueModules = Array.from(new Set(activityLogs.map(log => log.module).filter(Boolean)));

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-6 shadow-sm">
        <div className="mb-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 transition-colors hover:text-gray-900"
          >
            <MdArrowBack className="h-5 w-5" />
            <span className="text-sm font-medium">Back to Companies</span>
          </button>
        </div>

        <div className="flex items-center gap-4">
          {company.company_logo ? (
            <img
              src={company.company_logo}
              alt={company.company_name}
              className="h-16 w-16 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600">
              <MdBusiness className="h-8 w-8 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {company.company_name}
            </h1>
            <p className="text-sm text-gray-600">Company Dashboard</p>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 bg-white">
        <nav className="flex overflow-x-auto px-4 scrollbar-hide" aria-label="Tabs">
          {[
            { id: "overview", label: "Overview", icon: <MdBusiness className="h-3.5 w-3.5" /> },
            { id: "subscription", label: "Subscription", icon: <MdLayers className="h-3.5 w-3.5" /> },
            { id: "sso", label: "Single Sign-On", icon: <MdVpnKey className="h-3.5 w-3.5" /> },
            { id: "programs", label: "Programs", icon: <MdBugReport className="h-3.5 w-3.5" />, count: filteredPrograms.length },
            { id: "assets", label: "Assets", icon: <MdWebAsset className="h-3.5 w-3.5" />, count: assets.length },
            { id: "users", label: "Users", icon: <MdPeople className="h-3.5 w-3.5" />, count: company.users?.length || 0 },
            { id: "reports", label: "Reports", icon: <MdSecurity className="h-3.5 w-3.5" />, count: reports.length },
            { id: "pentest-reports", label: "Pentest Reports", icon: <MdDescription className="h-3.5 w-3.5" />, count: pentestReports.length },
            { id: "vault", label: "Vault", icon: <MdLock className="h-3.5 w-3.5" />, count: vaults.length },
            { id: "activity", label: "Activity", icon: <MdTimeline className="h-3.5 w-3.5" /> },
            { id: "calendar", label: "Calendar", icon: <MdCalendarToday className="h-3.5 w-3.5" /> },
            { id: "runs", label: "Runs", icon: <MdTimeline className="h-3.5 w-3.5" />, count: companyRuns.length },
            { id: "feedbacks", label: "Feedbacks", icon: <MdStar className="h-3.5 w-3.5" /> }
          ].filter(tab => {
            if (tab.id === "subscription") return isAuthorizedToManageRoles;
            if (tab.id === "sso") return isAuthorizedToManageRoles;
            if (tab.id === "feedbacks") return isAuthorizedToManageRoles;
            return true;
          }).map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3.5 text-xs font-medium transition-all whitespace-nowrap
                  ${isActive
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }
                `}
              >
                {tab.icon}
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none ${
                    isActive ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Dashboard Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
              <p className="text-gray-600">Loading dashboard...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Subscription Tab */}
            {activeTab === "subscription" && (
              <CompanySubscriptionTab 
                company={company} 
                programs={programs}
                reports={reports}
                pentestReports={pentestReports}
                companyRuns={companyRuns}
                assets={assets}
                vaults={vaults}
                onUpdate={() => window.location.reload()} 
              />
            )}

            {/* SSO Tab (CTB Staff Admin View) */}
            {activeTab === "sso" && (
              <AdminCompanySSOTab companyId={company.company_id} />
            )}

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {/* Business Owners */}
                  {/* <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Business Owners
                    </p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">
                      {businessOwners.length}
                    </p>
                  </div>
                  <div className="rounded-full bg-blue-100 p-3">
                    <MdBusiness className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4">
                  {businessOwners.slice(0, 2).map(owner => (
                    <div
                      key={owner.user_id}
                      className="mt-2 flex items-center gap-2"
                    >
                      <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                      <span className="text-sm text-gray-700">
                        {owner.username}
                      </span>
                    </div>
                  ))}
                  {businessOwners.length > 2 && (
                    <p className="mt-2 text-xs text-gray-500">
                      +{businessOwners.length - 2} more
                    </p>
                  )}
                </div>
              </div> */}

                  {/* Invited Users */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Invited Users
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {invitedUsers.length}
                        </p>
                      </div>
                      <div className="rounded-full bg-green-100 p-3">
                        <MdPeople className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">
                        Total team members: {company.users?.length || 0}
                      </p>
                    </div>
                  </div>

                  {/* Programs */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Total Programs
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {filteredPrograms.length}
                        </p>
                      </div>
                      <div className="rounded-full bg-purple-100 p-3">
                        <MdTimeline className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="mt-4 flex gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Active</p>
                        <p className="text-lg font-semibold text-green-600">
                          {activeProgramsCount}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Inactive</p>
                        <p className="text-lg font-semibold text-gray-400">
                          {inactiveProgramsCount}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Vulnerabilities */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Vulnerabilities
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {vulnerabilityStats.total}
                        </p>
                      </div>
                      <div className="rounded-full bg-red-100 p-3">
                        <MdSecurity className="h-6 w-6 text-red-600" />
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {/* Critical */}
                      <button
                        onClick={() => setSelectedSeverity(selectedSeverity === "critical" ? null : "critical")}
                        className={`rounded-lg border p-2 text-left transition-all hover:shadow-md ${selectedSeverity === "critical"
                          ? "border-red-600 bg-red-50"
                          : "border-gray-200 hover:border-red-300"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs font-medium text-red-600">
                            <MdError className="h-3 w-3" /> Critical
                          </span>
                          <span className="text-sm font-bold text-red-600">{vulnerabilityStats.critical}</span>
                        </div>
                      </button>

                      {/* High */}
                      <button
                        onClick={() => setSelectedSeverity(selectedSeverity === "high" ? null : "high")}
                        className={`rounded-lg border p-2 text-left transition-all hover:shadow-md ${selectedSeverity === "high"
                          ? "border-orange-600 bg-orange-50"
                          : "border-gray-200 hover:border-orange-300"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs font-medium text-orange-600">
                            <MdWarning className="h-3 w-3" /> High
                          </span>
                          <span className="text-sm font-bold text-orange-600">{vulnerabilityStats.high}</span>
                        </div>
                      </button>
                    </div>
                    {selectedSeverity && (
                      <div className="mt-3 text-center">
                        <p className="text-xs text-gray-500">
                          Filtering by {selectedSeverity} severity
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Pentest Reports */}
                  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Pentest Reports
                        </p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">
                          {pentestReportsCount}
                        </p>
                      </div>
                      <div className="rounded-full bg-indigo-100 p-3">
                        <MdDescription className="h-6 w-6 text-indigo-600" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-gray-600">
                        Generated automated reports
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions Required Section - Clean Table Design */}
                <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                  <div className="border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-gray-900">Actions Required</h2>
                    </div>

                    {/* Tabs */}
                    <div className="mt-4 flex gap-2 border-b border-gray-200">
                      <button
                        onClick={() => setActionTab("for-me")}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${actionTab === "for-me"
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "text-gray-600 hover:text-gray-900"
                          }`}
                      >
                        For Me (Admin)
                      </button>
                      <button
                        onClick={() => setActionTab("by-company")}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${actionTab === "by-company"
                          ? "border-b-2 border-blue-600 text-blue-600"
                          : "text-gray-600 hover:text-gray-900"
                          }`}
                      >
                        By Company
                      </button>
                    </div>
                  </div>

                  {/* For Me Tab */}
                  {actionTab === "for-me" && (
                    <>
                      <div className="border-b border-gray-200 px-6 py-3">
                        <div className="flex items-center justify-between">
                          {(() => {
                            const today = new Date();
                            let actionCount = 0;

                            // Count reports under review
                            actionCount += reports.filter(r => r.state === "underReview").length;

                            // Count critical severity reports
                            actionCount += reports.filter(r => r.severity_category === "critical" && r.state !== "closed").length;

                            // Count programs past end date
                            programs.forEach(program => {
                              if (program.expected_end_date && program.isActivated) {
                                const endDate = new Date(program.expected_end_date);
                                if (today > endDate) actionCount++;
                              }
                            });

                            // Count high severity reports
                            actionCount += reports.filter(r => r.severity_category === "high" && r.state !== "closed").length;

                            // Count new reports
                            actionCount += reports.filter(r => r.state === "new" || r.state === "triaged").length;

                            // Count programs ending soon
                            programs.forEach(program => {
                              if (program.expected_end_date && program.isActivated) {
                                const endDate = new Date(program.expected_end_date);
                                const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                if (daysUntilEnd > 0 && daysUntilEnd <= 7) actionCount++;
                              }
                            });

                            // Count programs starting soon
                            programs.forEach(program => {
                              if (program.expected_start_date) {
                                const startDate = new Date(program.expected_start_date);
                                const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                if (daysUntilStart > 0 && daysUntilStart <= 7) actionCount++;
                              }
                            });

                            // Count medium severity reports
                            actionCount += reports.filter(r => r.severity_category === "medium" && r.state !== "closed").length;

                            // Count programs without researchers
                            actionCount += programs.filter(p => p.isActivated && (!p.privateAccessUsers || p.privateAccessUsers.length === 0)).length;

                            // Count programs without dates
                            actionCount += programs.filter(p => p.isActivated && (!p.expected_start_date || !p.expected_end_date)).length;

                            // Count inactive programs with active reports
                            programs.forEach(program => {
                              if (!program.isActivated) {
                                const programReports = reports.filter(r => r.program_id === program.id && r.state !== "closed");
                                if (programReports.length > 0) actionCount++;
                              }
                            });

                            // Count programs with no activity
                            const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                            programs.forEach(program => {
                              if (program.isActivated) {
                                const programReports = reports.filter(r => r.program_id === program.id);
                                const recentReports = programReports.filter(r => {
                                  if (!r.submitted_date) return false;
                                  const submittedDate = new Date(r.submitted_date);
                                  return submittedDate >= thirtyDaysAgo;
                                });
                                if (recentReports.length === 0 && programReports.length === 0) actionCount++;
                              }
                            });

                            // Count pending pentest reports
                            actionCount += pentestReports.filter(pr => pr.status !== "business_review").length;

                            // Count low severity reports
                            actionCount += reports.filter(r => r.severity_category === "low" && r.state !== "closed").length;

                            return actionCount > 0 ? (
                              <span className="inline-flex items-center rounded-md bg-red-50 px-2.5 py-1 text-sm font-medium text-red-700">
                                {actionCount} {actionCount === 1 ? 'item' : 'items'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-md bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700">
                                All clear
                              </span>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        {(() => {
                          const today = new Date();
                          const actions: Array<{
                            priority: number;
                            priorityLabel: string;
                            type: string;
                            title: string;
                            description: string;
                            link: string;
                            icon: JSX.Element;
                            color: string;
                          }> = [];

                          // Priority 1: Reports Under Review (CRITICAL) - List each report individually
                          const reportsUnderReview = reports.filter(r => r.state === "underReview");
                          reportsUnderReview.forEach(report => {
                            actions.push({
                              priority: 1,
                              priorityLabel: "Critical",
                              type: "Report Review",
                              title: report.title || `Report #${report.report_id}`,
                              description: "Admin review and approval required",
                              link: `/dashboard/reports/${report.report_id}`,
                              icon: <MdDescription className="h-5 w-5" />,
                              color: "red"
                            });
                          });

                          // Priority 2: Critical Severity Reports - List each report individually
                          const criticalReports = reports.filter(r => r.severity_category === "critical" && r.state !== "closed");
                          criticalReports.forEach(report => {
                            actions.push({
                              priority: 2,
                              priorityLabel: "Critical",
                              type: "Security",
                              title: report.title || `Report #${report.report_id}`,
                              description: "Critical security vulnerability",
                              link: `/dashboard/reports/${report.report_id}`,
                              icon: <MdError className="h-5 w-5" />,
                              color: "red"
                            });
                          });

                          // Priority 3: Programs Past End Date (HIGH) - List each program individually
                          programs.forEach(program => {
                            if (program.expected_end_date && program.isActivated) {
                              const endDate = new Date(program.expected_end_date);
                              const daysOverdue = Math.ceil((today.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
                              if (daysOverdue > 0) {
                                actions.push({
                                  priority: 3,
                                  priorityLabel: "High",
                                  type: "Program Overdue",
                                  title: program.title,
                                  description: `Ended ${daysOverdue} day${daysOverdue > 1 ? 's' : ''} ago - Deactivate program`,
                                  link: `/dashboard/programs/${program.id}`,
                                  icon: <MdWarning className="h-5 w-5" />,
                                  color: "orange"
                                });
                              }
                            }
                          });

                          // Priority 4: High Severity Reports - List each report individually
                          const highReports = reports.filter(r => r.severity_category === "high" && r.state !== "closed");
                          highReports.forEach(report => {
                            actions.push({
                              priority: 4,
                              priorityLabel: "High",
                              type: "Security",
                              title: report.title || `Report #${report.report_id}`,
                              description: "High severity security issue",
                              link: `/dashboard/reports/${report.report_id}`,
                              icon: <MdWarning className="h-5 w-5" />,
                              color: "orange"
                            });
                          });

                          // Priority 5: New Reports (HIGH) - List each report individually
                          const newReports = reports.filter(r => r.state === "new" || r.state === "triaged");
                          newReports.forEach(report => {
                            actions.push({
                              priority: 5,
                              priorityLabel: "High",
                              type: "New Report",
                              title: report.title || `Report #${report.report_id}`,
                              description: "New report submitted - needs triage",
                              link: `/dashboard/reports/${report.report_id}`,
                              icon: <MdDescription className="h-5 w-5" />,
                              color: "orange"
                            });
                          });

                          // Priority 6: Programs Ending Soon (HIGH) - List each program individually
                          programs.forEach(program => {
                            if (program.expected_end_date && program.isActivated) {
                              const endDate = new Date(program.expected_end_date);
                              const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                              if (daysUntilEnd > 0 && daysUntilEnd <= 7) {
                                actions.push({
                                  priority: 6,
                                  priorityLabel: "High",
                                  type: "Program Ending",
                                  title: program.title,
                                  description: `Ends in ${daysUntilEnd} day${daysUntilEnd > 1 ? 's' : ''} - Prepare closure`,
                                  link: `/dashboard/programs/${program.id}`,
                                  icon: <MdCalendarToday className="h-5 w-5" />,
                                  color: "orange"
                                });
                              }
                            }
                          });

                          // Priority 7: Programs Starting Soon (MEDIUM) - List each program individually
                          programs.forEach(program => {
                            if (program.expected_start_date) {
                              const startDate = new Date(program.expected_start_date);
                              const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                              if (daysUntilStart > 0 && daysUntilStart <= 7) {
                                actions.push({
                                  priority: 7,
                                  priorityLabel: "Medium",
                                  type: "Program Starting",
                                  title: program.title,
                                  description: `Starts in ${daysUntilStart} day${daysUntilStart > 1 ? 's' : ''} - Verify setup`,
                                  link: `/dashboard/programs/${program.id}`,
                                  icon: <MdCalendarToday className="h-5 w-5" />,
                                  color: "blue"
                                });
                              }
                            }
                          });

                          // Priority 8: Medium Severity Reports - List each report individually
                          const mediumReports = reports.filter(r => r.severity_category === "medium" && r.state !== "closed");
                          mediumReports.forEach(report => {
                            actions.push({
                              priority: 8,
                              priorityLabel: "Medium",
                              type: "Security",
                              title: report.title || `Report #${report.report_id}`,
                              description: "Medium severity security issue",
                              link: `/dashboard/reports/${report.report_id}`,
                              icon: <MdWarning className="h-5 w-5" />,
                              color: "blue"
                            });
                          });

                          // Priority 9: Programs Without Assigned Researchers (MEDIUM)
                          programs.forEach(program => {
                            if (program.isActivated && (!program.privateAccessUsers || program.privateAccessUsers.length === 0)) {
                              actions.push({
                                priority: 9,
                                priorityLabel: "Medium",
                                type: "Program Setup",
                                title: program.title,
                                description: "No researchers assigned - assign team",
                                link: `/dashboard/programs/${program.id}`,
                                icon: <MdPeople className="h-5 w-5" />,
                                color: "blue"
                              });
                            }
                          });

                          // Priority 10: Programs Without Start/End Dates (MEDIUM)
                          programs.forEach(program => {
                            if (program.isActivated && (!program.expected_start_date || !program.expected_end_date)) {
                              actions.push({
                                priority: 10,
                                priorityLabel: "Medium",
                                type: "Program Setup",
                                title: program.title,
                                description: "Missing timeline dates - set schedule",
                                link: `/dashboard/programs/${program.id}`,
                                icon: <MdCalendarToday className="h-5 w-5" />,
                                color: "blue"
                              });
                            }
                          });

                          // Priority 11: Inactive Programs with Active Reports (MEDIUM)
                          programs.forEach(program => {
                            if (!program.isActivated) {
                              const programReports = reports.filter(r => r.program_id === program.id && r.state !== "closed");
                              if (programReports.length > 0) {
                                actions.push({
                                  priority: 11,
                                  priorityLabel: "Medium",
                                  type: "Program Status",
                                  title: program.title,
                                  description: `${programReports.length} active ${programReports.length === 1 ? 'report' : 'reports'} on inactive program`,
                                  link: `/dashboard/programs/${program.id}`,
                                  icon: <MdWarning className="h-5 w-5" />,
                                  color: "blue"
                                });
                              }
                            }
                          });

                          // Priority 12: Programs with No Activity (LOW)
                          programs.forEach(program => {
                            if (program.isActivated) {
                              const programReports = reports.filter(r => r.program_id === program.id);
                              const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                              const recentReports = programReports.filter(r => {
                                if (!r.submitted_date) return false;
                                const submittedDate = new Date(r.submitted_date);
                                return submittedDate >= thirtyDaysAgo;
                              });

                              if (recentReports.length === 0 && programReports.length === 0) {
                                actions.push({
                                  priority: 12,
                                  priorityLabel: "Low",
                                  type: "Program Activity",
                                  title: program.title,
                                  description: "No reports in 30+ days - check status",
                                  link: `/dashboard/programs/${program.id}`,
                                  icon: <MdTimeline className="h-5 w-5" />,
                                  color: "gray"
                                });
                              }
                            }
                          });

                          // Priority 13: Pentest Reports Pending Generation (LOW)
                          const pendingPentestReports = pentestReports.filter(pr => pr.status !== "business_review");
                          pendingPentestReports.forEach(pentestReport => {
                            actions.push({
                              priority: 13,
                              priorityLabel: "Low",
                              type: "Pentest Report",
                              title: pentestReport.title || `Pentest Report #${pentestReport.id}`,
                              description: `Status: ${pentestReport.status || 'pending'} - needs completion`,
                              link: `/dashboard/pentest-reports/${pentestReport.id}/edit`,
                              icon: <MdDescription className="h-5 w-5" />,
                              color: "gray"
                            });
                          });

                          // Priority 14: Low Severity Reports - List each report individually
                          const lowReports = reports.filter(r => r.severity_category === "low" && r.state !== "closed");
                          lowReports.forEach(report => {
                            actions.push({
                              priority: 14,
                              priorityLabel: "Low",
                              type: "Security",
                              title: report.title || `Report #${report.report_id}`,
                              description: "Low severity security issue",
                              link: `/dashboard/reports/${report.report_id}`,
                              icon: <MdBugReport className="h-5 w-5" />,
                              color: "gray"
                            });
                          });

                          // Sort by priority
                          actions.sort((a, b) => a.priority - b.priority);

                          return actions.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Priority
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Type
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Issue
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Action Required
                                  </th>
                                  <th scope="col" className="relative px-6 py-3">
                                    <span className="sr-only">View</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {actions.map((action, index) => (
                                  <tr
                                    key={index}
                                    className="cursor-pointer transition-colors hover:bg-gray-50"
                                    onClick={() => navigate(action.link)}
                                  >
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <span
                                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${action.color === "red" ? "bg-red-100 text-red-800" :
                                          action.color === "orange" ? "bg-orange-100 text-orange-800" :
                                            action.color === "yellow" ? "bg-yellow-100 text-yellow-800" :
                                              action.color === "blue" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                                          }`}
                                      >
                                        {action.priorityLabel}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <div className="flex items-center">
                                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${action.color === "red" ? "bg-red-100 text-red-600" :
                                          action.color === "orange" ? "bg-orange-100 text-orange-600" :
                                            action.color === "yellow" ? "bg-yellow-100 text-yellow-600" :
                                              action.color === "blue" ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"
                                          }`}>
                                          {action.icon}
                                        </div>
                                        <span className="ml-3 text-sm font-medium text-gray-900">{action.type}</span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm font-medium text-gray-900">{action.title}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-500">{action.description}</div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                      <button className="text-blue-600 hover:text-blue-900">
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="px-6 py-12 text-center">
                              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <h3 className="mt-2 text-sm font-medium text-gray-900">All clear</h3>
                              <p className="mt-1 text-sm text-gray-500">No actions required at this time</p>
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  )}

                  {/* By Company Tab */}
                  {actionTab === "by-company" && (
                    <div className="overflow-x-auto">
                      {(() => {
                        const today = new Date();
                        const companyActions: Array<{
                          priority: number;
                          priorityLabel: string;
                          type: string;
                          title: string;
                          description: string;
                          link: string;
                          icon: JSX.Element;
                          color: string;
                        }> = [];

                        // Priority 1: Reports awaiting fix without retest created (CRITICAL)
                        const awaitingFixReports = reports.filter(r => 
                          r.state === "awaiting_fix" || r.state === "Awaiting Fix"
                        );
                        awaitingFixReports.forEach(report => {
                          // Check if retest exists for this report
                          // Assuming we need to check if there's a related retest report
                          companyActions.push({
                            priority: 1,
                            priorityLabel: "Critical",
                            type: "Retest Required",
                            title: report.title || `Report #${report.report_id}`,
                            description: "Awaiting fix - retest not created",
                            link: `/dashboard/reports/${report.report_id}`,
                            icon: <MdWarning className="h-5 w-5" />,
                            color: "red"
                          });
                        });

                        // Priority 2: Programs ended without pentest report generated (HIGH)
                        programs.forEach(program => {
                          if (program.expected_end_date && program.type === "PTAAS") {
                            const endDate = new Date(program.expected_end_date);
                            const daysSinceEnd = Math.ceil((today.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));

                            if (daysSinceEnd > 0) {
                              // Check if pentest report exists for this program
                              // program_ids is a JSON array in automated_reports table
                              const hasPentestReport = pentestReports.some(pr => {
                                // Parse program_ids if it's a string, otherwise use as-is
                                const programIds = typeof pr.program_ids === 'string'
                                  ? JSON.parse(pr.program_ids)
                                  : pr.program_ids;

                                // Check if this program's id is in the program_ids array
                                return Array.isArray(programIds) &&
                                  programIds.includes(program.id) &&
                                  pr.status === "business_review";
                              });

                              if (!hasPentestReport) {
                                companyActions.push({
                                  priority: 2,
                                  priorityLabel: "High",
                                  type: "Pentest Report",
                                  title: program.title,
                                  description: `Ended ${daysSinceEnd} day${daysSinceEnd > 1 ? 's' : ''} ago - pentest report not generated`,
                                  link: `/dashboard/programs/${program.id}`,
                                  icon: <MdDescription className="h-5 w-5" />,
                                  color: "orange"
                                });
                              }
                            }
                          }
                        });

                        // Priority 3: Reports not taken action by company (MEDIUM)
                        const pendingCompanyAction = reports.filter(r =>
                          r.state === "accepted" || r.state === "triaged"
                        );
                        pendingCompanyAction.forEach(report => {
                          companyActions.push({
                            priority: 3,
                            priorityLabel: "Medium",
                            type: "Company Action",
                            title: report.title || `Report #${report.report_id}`,
                            description: "Waiting for company response/action",
                            link: `/dashboard/reports/${report.report_id}`,
                            icon: <MdBugReport className="h-5 w-5" />,
                            color: "blue"
                          });
                        });

                        // Sort by priority
                        companyActions.sort((a, b) => a.priority - b.priority);

                        return companyActions.length > 0 ? (
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Priority
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Issue
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Action Required
                                </th>
                                <th scope="col" className="relative px-6 py-3">
                                  <span className="sr-only">View</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {companyActions.map((action, index) => (
                                <tr
                                  key={index}
                                  className="cursor-pointer transition-colors hover:bg-gray-50"
                                  onClick={() => navigate(action.link)}
                                >
                                  <td className="whitespace-nowrap px-6 py-4">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${action.color === "red" ? "bg-red-100 text-red-800" :
                                      action.color === "orange" ? "bg-orange-100 text-orange-800" :
                                        action.color === "blue" ? "bg-blue-100 text-blue-800" :
                                          "bg-gray-100 text-gray-800"
                                      }`}>
                                      {action.priorityLabel}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <div className={`${action.color === "red" ? "text-red-600" :
                                        action.color === "orange" ? "text-orange-600" :
                                          action.color === "blue" ? "text-blue-600" :
                                            "text-gray-600"
                                        }`}>
                                        {action.icon}
                                      </div>
                                      <span className="text-sm font-medium text-gray-900">{action.type}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{action.title}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-600">{action.description}</div>
                                  </td>
                                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                    <span className="text-blue-600 hover:text-blue-900">View →</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="px-6 py-12 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">All clear</h3>
                            <p className="mt-1 text-sm text-gray-500">Company has no pending actions</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Recent Activity Feed */}
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
                    <button className="text-sm text-blue-600 hover:text-blue-700">
                      View All
                    </button>
                  </div>
                  <div className="space-y-4">
                    {/* Activity items - showing last 5 activities */}
                    {programs.slice(0, 5).map((program, index) => (
                      <div key={program.id} className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0">
                        <div className={`mt-1 rounded-full p-2 ${index === 0 ? "bg-blue-100" : "bg-gray-100"
                          }`}>
                          <MdTimeline className={`h-4 w-4 ${index === 0 ? "text-blue-600" : "text-gray-600"
                            }`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">
                            <span className="font-medium">Program created:</span> {program.title}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {moment(program.createdAt).fromNow()}
                          </p>
                        </div>
                      </div>
                    ))}
                    {programs.length === 0 && (
                      <div className="py-8 text-center">
                        <MdTimeline className="mx-auto h-12 w-12 text-gray-300" />
                        <p className="mt-2 text-sm text-gray-500">No recent activity</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Feedbacks Tab */}
            {activeTab === "feedbacks" && isAuthorizedToManageRoles && (
              <ProgramFeedbackManagement companyUserIds={company.users?.map((u: any) => u.user_id).join(',')} />
            )}

            {/* Programs Tab */}
            {activeTab === "programs" && (
              <div className="space-y-6">
                {/* Filters and Search */}
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">All Programs</h2>
                      <span className="text-sm text-gray-600">
                        {filteredPrograms.length} of {programs.length} programs
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        sessionStorage.setItem("ctb_admin_active_company_id", String(company.company_id));
                        if (company.company_name) {
                          sessionStorage.setItem("ctb_admin_active_company_name", company.company_name);
                        }
                        navigate(`/dashboard/create-program?company_id=${company.company_id}`);
                      }}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      Create a Program
                    </button>
                  </div>

                  {/* Search and Filters */}
                  <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                    {/* Search */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Search
                      </label>
                      <input
                        type="text"
                        placeholder="Search programs..."
                        value={programSearch}
                        onChange={(e) => setProgramSearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    {/* Type Filter */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Type
                      </label>
                      <select
                        value={programTypeFilter}
                        onChange={(e) => setProgramTypeFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all">All Types</option>
                        <option value="Bug Bounty">Bug Bounty</option>
                        <option value="PTAAS">PTaaS</option>
                        <option value="VDP">VDP</option>
                      </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <select
                        value={programStatusFilter}
                        onChange={(e) => setProgramStatusFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Testing Type Filter */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Testing Type
                      </label>
                      <select
                        value={programTestingTypeFilter}
                        onChange={(e) => setProgramTestingTypeFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all">All Testing Types</option>
                        <option value="Black Box">Black Box</option>
                        <option value="White Box">White Box</option>
                        <option value="Grey Box">Grey Box</option>
                      </select>
                    </div>

                    {/* Sort By */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Sort By
                      </label>
                      <select
                        value={programSortBy}
                        onChange={(e) => setProgramSortBy(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="date_desc">Newest First</option>
                        <option value="date_asc">Oldest First</option>
                        <option value="name_asc">Name (A-Z)</option>
                        <option value="name_desc">Name (Z-A)</option>
                        <option value="status">Active First</option>
                      </select>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  {(programSearch || programTypeFilter !== "all" || programStatusFilter !== "all" || programTestingTypeFilter !== "all" || programSortBy !== "date_desc") && (
                    <div className="mb-4">
                      <button
                        onClick={() => {
                          setProgramSearch("");
                          setProgramTypeFilter("all");
                          setProgramStatusFilter("all");
                          setProgramTestingTypeFilter("all");
                          setProgramSortBy("date_desc");
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Clear all filters & reset sort
                      </button>
                    </div>
                  )}

                  {/* Programs Grid */}
                  {filteredPrograms.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {sortedPrograms.map(program => (
                        <ProgramCard
                          key={program.id}
                          program={program}
                          onClick={() => navigate(`/dashboard/programs/${program.id}`)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <MdBugReport className="mx-auto h-12 w-12 text-gray-300" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        {programs.length === 0 ? "No programs yet" : "No programs match your filters"}
                      </h3>
                      <p className="text-gray-600">
                        {programs.length === 0
                          ? "This company hasn't created any programs yet."
                          : "Try adjusting your filters to see more results."}
                      </p>
                      {programs.length === 0 && (
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              sessionStorage.setItem("ctb_admin_active_company_id", String(company.company_id));
                              if (company.company_name) {
                                sessionStorage.setItem("ctb_admin_active_company_name", company.company_name);
                              }
                              navigate(`/dashboard/create-program?company_id=${company.company_id}`);
                            }}
                            className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                          >
                            Create a Program
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === "users" && (
              <div className="space-y-6">
                {/* Invitation Form for Admin */}
                {isAdmin && businessOwners.length > 0 && (
                  <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Invite User on Behalf of Business Owner
                      </h3>
                      {!showInvitationForm && (
                        <button
                          onClick={() => setShowInvitationForm(true)}
                          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                        >
                          <MdAdd className="h-5 w-5" />
                          Create Invitation
                        </button>
                      )}
                    </div>

                    {showInvitationForm && (
                      <div className="space-y-4 border-t border-gray-200 pt-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          {/* Business Owner Selection */}
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Business Owner <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selectedBusinessOwner || ""}
                              onChange={(e) => setSelectedBusinessOwner(Number(e.target.value))}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={invitationLoading}
                            >
                              <option value="">Select Business Owner</option>
                              {businessOwners.map((owner) => (
                                <option key={owner.user_id} value={owner.user_id}>
                                  {owner.username} ({owner.email})
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                              The invitation will be sent as if this business owner invited the user
                            </p>
                          </div>

                          {/* Email Input */}
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              placeholder="user@example.com"
                              value={invitationEmail}
                              onChange={(e) => setInvitationEmail(e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={invitationLoading}
                            />
                          </div>

                          {/* Role Selection */}
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Role <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={invitationRole}
                              onChange={(e) => setInvitationRole(Number(e.target.value) as UserRole)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={invitationLoading}
                            >
                              {ROLE_OPTIONS[UserRole.BUSINESS].map((roleOption) => (
                                <option key={roleOption.value} value={roleOption.value}>
                                  {roleOption.label}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                              Business owners can invite: Security Engineer, Program Manager, Organization Admin
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3 pt-2">
                          <button
                            onClick={handleCreateInvitation}
                            disabled={invitationLoading || !invitationEmail || !selectedBusinessOwner}
                            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {invitationLoading ? "Sending..." : "Send Invitation"}
                          </button>
                          <button
                            onClick={() => {
                              setShowInvitationForm(false);
                              setInvitationEmail("");
                              setInvitationRole(UserRole.DEVELOPER);
                              setSelectedBusinessOwner(null);
                            }}
                            disabled={invitationLoading}
                            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <MdClose className="h-4 w-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Company Users</h2>
                    <span className="text-sm text-gray-600">
                      {filteredUsers.length} of {company.users?.length || 0} users
                    </span>
                  </div>

                  {/* Search and Filters */}
                  <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Search */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Search
                      </label>
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    {/* Role Filter */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Role
                      </label>
                      <select
                        value={userRoleFilter}
                        onChange={(e) => setUserRoleFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all">All Roles</option>
                        <option value="2">Business</option>
                        <option value="8">Business Manager</option>
                        <option value="9">Business Administrator</option>
                      </select>
                    </div>

                    {/* Type Filter */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        User Type
                      </label>
                      <select
                        value={userTypeFilter}
                        onChange={(e) => setUserTypeFilter(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="all">All Types</option>
                        <option value="owner">Owners Only</option>
                        <option value="subuser">Sub-users Only</option>
                      </select>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  {(userSearch || userRoleFilter !== "all" || userTypeFilter !== "all") && (
                    <div className="mb-4">
                      <button
                        onClick={() => {
                          setUserSearch("");
                          setUserRoleFilter("all");
                          setUserTypeFilter("all");
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700"
                      >
                        Clear all filters
                      </button>
                    </div>
                  )}

                  {/* Users Table */}
                  {filteredUsers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              User
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Joined
                            </th>
                            {isAuthorizedToManageRoles && (
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Actions
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {filteredUsers.map((user) => (
                            <tr key={user.user_id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-6 py-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.username}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                {isAuthorizedToManageRoles &&
                                  user.user_id !== currentUserId &&
                                  !(user.role === 2 && !user.parent_user_id) ? (
                                  <select
                                    value={user.role}
                                    onChange={(e) => handleUpdateRole(user.user_id, Number(e.target.value) as UserRole)}
                                    disabled={updatingRoleUserId === user.user_id}
                                    className="rounded-lg border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                                  >
                                    {ROLE_OPTIONS[UserRole.BUSINESS].map((roleOption) => (
                                      <option key={roleOption.value} value={roleOption.value}>
                                        {roleOption.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                                    {formatRole(user.role as UserRole)}
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${user.parent_user_id === null
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-green-100 text-green-800"
                                  }`}>
                                  {user.parent_user_id === null ? "Owner" : "Sub-user"}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {user.created_at ? moment(user.created_at).format("MMM DD, YYYY") : "N/A"}
                              </td>
                              {isAuthorizedToManageRoles && (
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                  {user.user_id !== currentUserId && !(user.role === 2 && !user.parent_user_id) ? (
                                    <span className="text-xs text-gray-400 italic">Manage Role in Role column</span>
                                  ) : (
                                    <span className="text-xs text-gray-400">---</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <MdPeople className="mx-auto h-12 w-12 text-gray-300" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        {company.users?.length === 0 ? "No users yet" : "No users match your filters"}
                      </h3>
                      <p className="text-gray-600">
                        {company.users?.length === 0
                          ? "This company has no users."
                          : "Try adjusting your filters to see more results."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === "reports" && (
              <div className="space-y-6">
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Vulnerability Reports</h2>
                    <span className="text-sm text-gray-600">
                      {filteredReports.length} of {reports.length} reports
                    </span>
                  </div>

                  {reportsLoading ? (
                    <div className="py-12 text-center">
                      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
                      <p className="text-gray-600">Loading reports...</p>
                    </div>
                  ) : (
                    <>
                      {/* Search and Filters */}
                      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {/* Search */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Search
                          </label>
                          <input
                            type="text"
                            placeholder="Search by title or ID..."
                            value={reportSearch}
                            onChange={(e) => setReportSearch(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>

                        {/* Severity Filter */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Severity
                          </label>
                          <select
                            value={reportSeverityFilter}
                            onChange={(e) => setReportSeverityFilter(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="all">All Severities</option>
                            <option value="CRITICAL">Critical</option>
                            <option value="HIGH">High</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="LOW">Low</option>
                            <option value="INFO">Info</option>
                          </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Status
                          </label>
                          <select
                            value={reportStatusFilter}
                            onChange={(e) => setReportStatusFilter(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="all">All Status</option>
                            <option value="NEW">New</option>
                            <option value="TRIAGED">Triaged</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="RESOLVED">Resolved</option>
                            <option value="CLOSED">Closed</option>
                            <option value="ARCHIVED">Archived</option>
                          </select>
                        </div>

                        {/* Program Filter */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Program
                          </label>
                          <select
                            value={reportProgramFilter}
                            onChange={(e) => setReportProgramFilter(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="all">All Programs</option>
                            {programs.map(program => (
                              <option key={program.id} value={program.id.toString()}>
                                {program.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Clear Filters Button */}
                      {(reportSearch || reportSeverityFilter !== "all" || reportStatusFilter !== "all" || reportProgramFilter !== "all") && (
                        <div className="mb-4">
                          <button
                            onClick={() => {
                              setReportSearch("");
                              setReportSeverityFilter("all");
                              setReportStatusFilter("all");
                              setReportProgramFilter("all");
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Clear all filters
                          </button>
                        </div>
                      )}

                      {/* Reports Table */}
                      {filteredReports.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Title
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Severity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Program
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Submitted
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {filteredReports.map((report) => {
                                const program = programs.find(p => p.id === report.program_id);
                                const severityColors: Record<string, string> = {
                                  CRITICAL: "bg-red-100 text-red-800",
                                  HIGH: "bg-orange-100 text-orange-800",
                                  MEDIUM: "bg-yellow-100 text-yellow-800",
                                  LOW: "bg-blue-100 text-blue-800",
                                  INFO: "bg-gray-100 text-gray-800"
                                };

                                return (
                                  <tr
                                    key={report.report_id}
                                    className="cursor-pointer transition-colors hover:bg-gray-50"
                                    onClick={() => navigate(`/dashboard/reports/${report.report_id}`)}
                                  >
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        #{report.report_id}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-900">
                                        {report.report_title || "Untitled Report"}
                                      </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${severityColors[report.severity_category] || "bg-gray-100 text-gray-800"
                                        }`}>
                                        {report.severity_category}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                                        {report.state || "N/A"}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-500">
                                        {program?.title || "Unknown Program"}
                                      </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                      {report.submitted_date ? moment(report.submitted_date).format("MMM DD, YYYY") : "N/A"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="py-12 text-center">
                          <MdSecurity className="mx-auto h-12 w-12 text-gray-300" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            {reports.length === 0 ? "No reports yet" : "No reports match your filters"}
                          </h3>
                          <p className="text-gray-600">
                            {reports.length === 0
                              ? "No vulnerability reports have been submitted yet."
                              : "Try adjusting your filters to see more results."}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Pentest Reports Tab */}
            {activeTab === "pentest-reports" && (
              <div className="space-y-6">
                {/* Header with Filters */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600 shadow-md">
                          <MdDescription className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-gray-900">Pentest Reports</h2>
                          <p className="text-xs text-gray-600">
                            {pentestReports.filter(pr => pr.status === pentestStatusFilter || pentestStatusFilter === "all").length} reports available
                          </p>
                        </div>
                      </div>

                      {/* Create Scoped/Combined Report Button */}
                      {(currentUserRole === UserRole.ADMIN ||
                        currentUserRole === UserRole.SUB_ADMIN ||
                        currentUserRole === UserRole.QA) && (
                        <button
                          onClick={() => setShowScopedReportModal(true)}
                          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-indigo-750 transition-colors"
                        >
                          <MdAdd className="h-5 w-5" />
                          Create Scoped/Combined Report
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {/* Search */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Search Reports
                        </label>
                        <input
                          type="text"
                          placeholder="Search by title or company..."
                          value={pentestSearch}
                          onChange={(e) => setPentestSearch(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>

                      {/* Status Filter */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          value={pentestStatusFilter}
                          onChange={(e) => setPentestStatusFilter(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="all">All Statuses</option>
                          <option value="business_review">Business Review</option>
                          <option value="approved">Approved</option>
                          <option value="pending">Pending</option>
                          <option value="draft">Draft</option>
                          <option value="qa_review">QA Review</option>
                          <option value="admin_review">Admin Review</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    </div>

                    {/* Clear Filters */}
                    {(pentestSearch || pentestStatusFilter !== "all") && (
                      <div className="mt-3">
                        <button
                          onClick={() => {
                            setPentestSearch("");
                            setPentestStatusFilter("all");
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-700"
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Reports Grid */}
                  <div className="p-6">
                    {(() => {
                      // Filter pentest reports
                      const filteredPentestReports = pentestReports.filter(report => {
                        const matchesSearch = !pentestSearch ||
                          report.title?.toLowerCase().includes(pentestSearch.toLowerCase()) ||
                          report.company_name?.toLowerCase().includes(pentestSearch.toLowerCase());

                        const matchesStatus = pentestStatusFilter === "all" || report.status === pentestStatusFilter;

                        return matchesSearch && matchesStatus;
                      });

                      return filteredPentestReports.length > 0 ? (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {filteredPentestReports.map((report) => {
                            const statusColors: Record<string, string> = {
                              business_review: "bg-green-100 text-green-800 border-green-200",
                              approved: "bg-blue-100 text-blue-800 border-blue-200",
                              pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
                              draft: "bg-gray-100 text-gray-800 border-gray-200",
                              qa_review: "bg-purple-100 text-purple-800 border-purple-200",
                              admin_review: "bg-orange-100 text-orange-800 border-orange-200",
                              rejected: "bg-red-100 text-red-800 border-red-200"
                            };

                            return (
                              <div
                                key={report.id}
                                className="group cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md"
                                onClick={() => navigate(`/dashboard/pentest-reports/${report.id}/edit`)}
                              >
                                {/* Card Header */}
                                <div className="border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50 px-4 py-3">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h3 className="font-semibold text-gray-900 group-hover:text-indigo-600">
                                        {report.title || "Untitled Report"}
                                      </h3>
                                      <p className="mt-1 text-xs text-gray-600">
                                        {report.company_name}
                                      </p>
                                    </div>
                                    <MdDescription className="h-5 w-5 text-indigo-600" />
                                  </div>
                                </div>

                                {/* Card Body */}
                                <div className="p-4">
                                  {/* Status Badge & Flow Badge */}
                                  <div className="mb-3 flex flex-wrap gap-2">
                                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColors[report.status] || "bg-gray-100 text-gray-800 border-gray-200"
                                      }`}>
                                      {report.status?.replace(/_/g, " ").toUpperCase()}
                                    </span>
                                    {(currentUserRole === UserRole.ADMIN ||
                                      currentUserRole === UserRole.SUB_ADMIN ||
                                      currentUserRole === UserRole.ADMIN_MANAGER ||
                                      currentUserRole === UserRole.QA) && (
                                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${report.report_suffix
                                          ? "bg-indigo-150 text-indigo-800 border-indigo-250"
                                          : "bg-gray-100 text-gray-750 border-gray-200"
                                        }`}>
                                        {report.report_suffix ? "COMBINED REPORT" : "DEFAULT REPORT"}
                                      </span>
                                    )}
                                  </div>

                                  {/* Findings Summary */}
                                  <div className="mb-3 grid grid-cols-2 gap-2">
                                    <div className="rounded-lg bg-gray-50 px-3 py-2">
                                      <p className="text-xs text-gray-600">Total Findings</p>
                                      <p className="text-lg font-bold text-gray-900">{report.total_findings || 0}</p>
                                    </div>
                                    <div className="rounded-lg bg-red-50 px-3 py-2">
                                      <p className="text-xs text-red-600">Critical</p>
                                      <p className="text-lg font-bold text-red-900">{report.critical_count || 0}</p>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-3 gap-2">
                                    <div className="rounded-lg bg-orange-50 px-2 py-1.5">
                                      <p className="text-xs text-orange-600">High</p>
                                      <p className="text-sm font-bold text-orange-900">{report.high_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg bg-yellow-50 px-2 py-1.5">
                                      <p className="text-xs text-yellow-600">Medium</p>
                                      <p className="text-sm font-bold text-yellow-900">{report.medium_count || 0}</p>
                                    </div>
                                    <div className="rounded-lg bg-blue-50 px-2 py-1.5">
                                      <p className="text-xs text-blue-600">Low</p>
                                      <p className="text-sm font-bold text-blue-900">{report.low_count || 0}</p>
                                    </div>
                                  </div>

                                  {/* Programs */}
                                  {report.program_ids && Array.isArray(report.program_ids) && report.program_ids.length > 0 && (
                                    <div className="mt-3 border-t border-gray-100 pt-3">
                                      <p className="text-xs font-medium text-gray-600">Program(s):</p>
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {report.program_ids.slice(0, 3).map((programId: number) => {
                                          const program = programs.find(p => p.id === programId);
                                          const programTitle = program?.title || report.program_names?.[programId] || `#${programId}`;
                                          return (
                                            <span
                                              key={programId}
                                              className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800"
                                            >
                                              {programTitle}
                                            </span>
                                          );
                                        })}
                                        {report.program_ids.length > 3 && (
                                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                            +{report.program_ids.length - 3} more
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Runs */}
                                  {((report.run_ids && Array.isArray(report.run_ids) && report.run_ids.length > 0) || report.run_id) && (
                                    <div className="mt-3 border-t border-gray-100 pt-3">
                                      <p className="text-xs font-medium text-gray-600">Runs:</p>
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {(() => {
                                          let runIdsToDisplay = [];
                                          if (report.run_ids && Array.isArray(report.run_ids)) {
                                            runIdsToDisplay = report.run_ids;
                                          } else if (report.run_id) {
                                            runIdsToDisplay = [report.run_id];
                                          }

                                          return (
                                            <>
                                              {runIdsToDisplay.slice(0, 3).map((runId: number) => {
                                                const run = allRuns.find(r => r.run_id === runId);
                                                return (
                                                  <span
                                                    key={runId}
                                                    className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 border border-emerald-250"
                                                  >
                                                    {run?.run_title || `Run #${runId}`}
                                                  </span>
                                                );
                                              })}
                                              {runIdsToDisplay.length > 3 && (
                                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                                  +{runIdsToDisplay.length - 3} more
                                                </span>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  )}

                                  {/* Date Range Scoped Indicator */}
                                  {report.start_date && report.end_date && (
                                    <div className="mt-3 border-t border-gray-100 pt-3 bg-indigo-50/20 rounded-lg p-2 border border-indigo-100/50">
                                      <p className="text-[10px] uppercase font-bold text-indigo-750">
                                        Date Range Scoped:
                                      </p>
                                      <p className="text-xs text-gray-700 mt-0.5 font-medium">
                                        {moment(report.start_date).format("MMM DD, YYYY")} - {moment(report.end_date).format("MMM DD, YYYY")}
                                      </p>
                                    </div>
                                  )}

                                  {/* Date */}
                                  {report.createdAt && (
                                    <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
                                      <MdCalendarToday className="h-3 w-3" />
                                      <span>Created {moment(report.createdAt).format("MMM DD, YYYY")}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Card Footer */}
                                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-600">
                                      Doc: {report.document_number || "N/A"}
                                    </span>
                                    <span className="font-medium text-indigo-600 group-hover:text-indigo-700">
                                      View Report →
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="py-16 text-center">
                          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                            <MdDescription className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-base font-semibold text-gray-900">No pentest reports found</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {pentestSearch || pentestStatusFilter !== "business_review"
                              ? "Try adjusting your filters to see more results."
                              : "No pentest reports have been generated yet."}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === "activity" && (
              <div className="space-y-6">
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Activity Log</h2>
                    <span className="text-sm text-gray-600">
                      {filteredActivityLogs.length} of {activityLogs.length} activities
                    </span>
                  </div>

                  {activityLoading ? (
                    <div className="py-12 text-center">
                      <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
                      <p className="text-gray-600">Loading activity logs...</p>
                    </div>
                  ) : (
                    <>
                      {/* Filters */}
                      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                        {/* Module/Type Filter */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Module
                          </label>
                          <select
                            value={activityTypeFilter}
                            onChange={(e) => setActivityTypeFilter(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="all">All Modules</option>
                            {uniqueModules.map(module => (
                              <option key={module} value={module}>
                                {module}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* User Filter */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            User
                          </label>
                          <select
                            value={activityUserFilter}
                            onChange={(e) => setActivityUserFilter(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="all">All Users</option>
                            {(company.users || []).map(user => (
                              <option key={user.user_id} value={user.user_id.toString()}>
                                {user.username} ({user.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Clear Filters Button */}
                      {(activityTypeFilter !== "all" || activityUserFilter !== "all") && (
                        <div className="mb-4">
                          <button
                            onClick={() => {
                              setActivityTypeFilter("all");
                              setActivityUserFilter("all");
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Clear all filters
                          </button>
                        </div>
                      )}

                      {/* Activity Timeline */}
                      {filteredActivityLogs.length > 0 ? (
                        <div className="space-y-4">
                          {filteredActivityLogs.map((log, index) => {
                            const user = company.users?.find(u => u.user_id === log.user_id);
                            const moduleColors: Record<string, string> = {
                              Program: "bg-blue-100 text-blue-800",
                              Report: "bg-green-100 text-green-800",
                              User: "bg-purple-100 text-purple-800",
                              Invitation: "bg-yellow-100 text-yellow-800",
                              Role: "bg-orange-100 text-orange-800"
                            };

                            return (
                              <div
                                key={log.id || index}
                                className="flex gap-4 border-l-2 border-gray-200 pb-4 pl-4 last:border-l-0 last:pb-0"
                              >
                                <div className="flex-shrink-0">
                                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                                    <MdTimeline className="h-5 w-5 text-gray-600" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="text-sm text-gray-900">{log.action}</p>
                                      <div className="mt-1 flex items-center gap-2">
                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${moduleColors[log.module] || "bg-gray-100 text-gray-800"
                                          }`}>
                                          {log.module}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          by {user?.username || user?.email || "Unknown User"}
                                        </span>
                                      </div>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {log.created_at ? moment(log.created_at).fromNow() : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="py-12 text-center">
                          <MdTimeline className="mx-auto h-12 w-12 text-gray-300" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            {activityLogs.length === 0 ? "No activity yet" : "No activities match your filters"}
                          </h3>
                          <p className="text-gray-600">
                            {activityLogs.length === 0
                              ? "No activities have been logged for this company yet."
                              : "Try adjusting your filters to see more results."}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === "calendar" && (
              <div className="space-y-6">
                {/* Calendar View */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  {/* Header */}
                  <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 shadow-md">
                          <MdCalendarToday className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-gray-900">Program Timeline</h2>
                          <p className="text-xs text-gray-600">
                            {programs.filter(p => p.expected_start_date && p.expected_end_date).length} programs scheduled
                          </p>
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-purple-500"></div>
                          <span className="text-xs font-medium text-gray-600">Bug Bounty</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                          <span className="text-xs font-medium text-gray-600">PTaaS</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-green-500"></div>
                          <span className="text-xs font-medium text-gray-600">VDP</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Calendar Body */}
                  <div className="p-6">
                    {programs.filter(p => p.expected_start_date && p.expected_end_date).length > 0 ? (
                      <div className="rounded-lg border border-gray-200 bg-white" style={{ height: "650px" }}>
                        <style>{`
                          .rbc-calendar {
                            font-family: inherit;
                          }
                          .rbc-header {
                            padding: 12px 8px;
                            font-weight: 600;
                            font-size: 0.875rem;
                            color: #374151;
                            background: #F9FAFB;
                            border-bottom: 2px solid #E5E7EB;
                          }
                          .rbc-today {
                            background-color: #EFF6FF;
                          }
                          .rbc-off-range-bg {
                            background-color: #F9FAFB;
                          }
                          .rbc-event {
                            padding: 4px 8px;
                            border-radius: 6px;
                            font-size: 0.813rem;
                            font-weight: 500;
                            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                            transition: all 0.2s;
                          }
                          .rbc-event:hover {
                            transform: translateY(-1px);
                            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
                          }
                          .rbc-event-label {
                            font-size: 0.75rem;
                          }
                          .rbc-toolbar {
                            padding: 16px;
                            margin-bottom: 16px;
                            background: #F9FAFB;
                            border-radius: 8px;
                            border: 1px solid #E5E7EB;
                          }
                          .rbc-toolbar button {
                            color: #374151;
                            border: 1px solid #D1D5DB;
                            background: white;
                            padding: 6px 12px;
                            border-radius: 6px;
                            font-weight: 500;
                            transition: all 0.2s;
                          }
                          .rbc-toolbar button:hover {
                            background: #F3F4F6;
                            border-color: #9CA3AF;
                          }
                          .rbc-toolbar button.rbc-active {
                            background: #3B82F6;
                            color: white;
                            border-color: #3B82F6;
                          }
                          .rbc-toolbar button.rbc-active:hover {
                            background: #2563EB;
                            border-color: #2563EB;
                          }
                          .rbc-month-view {
                            border: 1px solid #E5E7EB;
                            border-radius: 8px;
                            overflow: hidden;
                          }
                          .rbc-day-bg {
                            border-left: 1px solid #F3F4F6;
                          }
                          .rbc-date-cell {
                            padding: 8px;
                            text-align: right;
                          }
                          .rbc-date-cell > a {
                            color: #6B7280;
                            font-weight: 500;
                          }
                          .rbc-today .rbc-date-cell > a {
                            color: #3B82F6;
                            font-weight: 700;
                          }
                        `}</style>
                        <Calendar
                          localizer={momentLocalizer(moment)}
                          events={programs
                            .filter(p => p.expected_start_date && p.expected_end_date)
                            .map(program => {
                              const typeColors: Record<string, string> = {
                                "Bug Bounty": "#A855F7",
                                "PTAAS": "#3B82F6",
                                "VDP": "#10B981"
                              };

                              return {
                                title: program.title,
                                start: new Date(program.expected_start_date!),
                                end: new Date(program.expected_end_date!),
                                resource: {
                                  program,
                                  color: typeColors[program.type || ""] || "#6B7280"
                                }
                              };
                            })}
                          startAccessor="start"
                          endAccessor="end"
                          style={{ height: "100%" }}
                          views={["month", "week", "day", "agenda"]}
                          defaultView="month"
                          eventPropGetter={(event: any) => ({
                            style: {
                              backgroundColor: event.resource.color,
                              borderRadius: "6px",
                              opacity: event.resource.program.isActivated ? 1 : 0.5,
                              color: "white",
                              border: "none",
                              display: "block",
                              fontWeight: "500"
                            }
                          })}
                          tooltipAccessor={(event: any) => {
                            const program = event.resource.program;
                            return `${program.title} (${program.type || "N/A"}) - ${program.isActivated ? "Active" : "Inactive"}`;
                          }}
                          onSelectEvent={(event: any) => {
                            navigate(`/dashboard/programs/${event.resource.program.id}`);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="py-16 text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                          <MdCalendarToday className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">No program timelines</h3>
                        <p className="mt-1 text-sm text-gray-500">Programs don't have start/end dates set yet.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resource Allocation Section */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  {/* Header */}
                  <div className="border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-600 shadow-md">
                          <MdPeople className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-gray-900">Resource Allocation</h2>
                          <p className="text-xs text-gray-600">
                            {programs.filter(p => p.privateAccessUsers && p.privateAccessUsers.length > 0).length} programs with assigned researchers
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className="p-6">
                    {programs.filter(p => p.privateAccessUsers && p.privateAccessUsers.length > 0).length > 0 ? (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Program
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Assigned Researchers
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Timeline
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {programs
                              .filter(p => p.privateAccessUsers && p.privateAccessUsers.length > 0)
                              .map((program) => {
                                const startDate = program.expected_start_date ? new Date(program.expected_start_date) : null;
                                const endDate = program.expected_end_date ? new Date(program.expected_end_date) : null;

                                return (
                                  <tr key={program.id} className="hover:bg-gray-50">
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <div className="text-sm font-medium text-gray-900">{program.title}</div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <span className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                                        {program.type || "N/A"}
                                      </span>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${program.isActivated ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                                        }`}>
                                        {program.isActivated ? "Active" : "Inactive"}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="flex flex-wrap gap-1">
                                        {program.privateAccessUsers && program.privateAccessUsers.length > 0 ? (
                                          program.privateAccessUsers.map((userId, index) => {
                                            // Try to get from researchersMap first, then fallback to company.users
                                            let researcher = researchersMap[userId];

                                            // Fallback: Look up in company.users if not in researchersMap
                                            if (!researcher && company.users) {
                                              const user = company.users.find((u: any) => u.user_id === userId) as any;
                                              if (user) {
                                                researcher = {
                                                  username: user.username || user.display_name || `User ${userId}`,
                                                  email: user.email || ""
                                                };
                                              }
                                            }

                                            console.log(`Looking up researcher ${userId}:`, researcher, "from map:", researchersMap);
                                            return (
                                              <span
                                                key={index}
                                                className="inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800"
                                                title={researcher?.email || `User ID: ${userId}`}
                                              >
                                                {researcher?.username || `Researcher #${userId}`}
                                              </span>
                                            );
                                          })
                                        ) : (
                                          <span className="text-sm text-gray-500">No researchers assigned</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                      {startDate && endDate ? (
                                        <div>
                                          <div>{moment(startDate).format("MMM DD")} - {moment(endDate).format("MMM DD, YYYY")}</div>
                                          <div className="text-xs text-gray-400">
                                            {moment(endDate).diff(moment(startDate), "days")} days
                                          </div>
                                        </div>
                                      ) : (
                                        "Not set"
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="py-16 text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                          <MdPeople className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">No resource allocations</h3>
                        <p className="mt-1 text-sm text-gray-500">No researchers have been assigned to programs yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "vault" && (
          <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Credential Vaults</h2>
                <p className="text-sm text-gray-500">View and manage program links for credential vaults.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <MdSecurity className="h-4 w-4 text-gray-400" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search vaults..."
                    className="block w-64 rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={vaultSearch}
                    onChange={(e) => setVaultSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {vaultsLoading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
              </div>
            ) : vaults.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Vault Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Entries</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Linked Programs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created At</th>
                      {isAuthorizedToManageRoles && (
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {vaults
                      .filter(v => v.name.toLowerCase().includes(vaultSearch.toLowerCase()) || v.description?.toLowerCase().includes(vaultSearch.toLowerCase()))
                      .map((vault) => {
                        const linked = linkedProgramsMap[vault.vault_id] || [];

                        return (
                          <tr key={vault.vault_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                                  <MdLock className="h-4 w-4" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{vault.name}</div>
                                  {vault.description && <div className="text-xs text-gray-500 line-clamp-1">{vault.description}</div>}
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                {vault.entries?.length || 0} credentials
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              <div className="flex flex-wrap gap-1.5 max-w-[300px]">
                                {linked.length > 0 ? (
                                  linked.map((lp) => (
                                    <span key={lp.programId} className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs border border-blue-100 font-medium">
                                      {lp.programTitle}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-xs italic">No programs linked</span>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                 <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 uppercase">
                                   {vault.creator?.username?.substring(0, 2) || "??"}
                                 </div>
                                 <span>{vault.creator?.username || "Unknown"}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                              {moment(vault.createdAt).format("MMM DD, YYYY")}
                            </td>
                            {isAuthorizedToManageRoles && (
                              <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                                <button
                                  onClick={() => {
                                    setSelectedLinkVault(vault);
                                    setShowLinkProgramModal(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                                >
                                  <FiLink className="w-3.5 h-3.5" />
                                  Manage Links
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-200 py-12 text-center text-gray-500">
                <MdLock className="mx-auto h-12 w-12 text-gray-300" />
                <p className="mt-2 font-medium">No vaults found</p>
                <p className="text-sm">This company hasn't created any credential vaults yet.</p>
              </div>
            )}
          </div>
        )}

                {/* Runs Tab */}
                {activeTab === "runs" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Program Runs & Quota</h2>
                        <p className="text-sm text-gray-500">Manage comprehensive run cycles and track quota usage for {company.company_name}.</p>
                      </div>
                      <button
                        onClick={() => fetchCompanyRunsAndQuota(true)}
                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <MdRefresh className={`h-4 w-4 ${runsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>

                    {/* Quota Overview Card */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {/* Comprehensive Quota Card */}
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 shadow-md">
                              <MdTimeline className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">Comprehensive Cycle</h3>
                              <p className="text-xs text-gray-600">Full inventory coverage tracking</p>
                            </div>
                          </div>
                          {(currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SUB_ADMIN) && (
                            <button
                              onClick={handleForceComplete}
                              disabled={forcingComplete}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                              {forcingComplete ? (
                                <MdRefresh className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <MdCheckCircle className="h-3.5 w-3.5" />
                              )}
                              Force Complete Cycle
                            </button>
                          )}
                          {(currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SUB_ADMIN) && companyQuota?.comprehensive?.canUndo && (
                            <button
                              onClick={handleUndoForceComplete}
                              disabled={undoingComplete}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 shadow-sm hover:bg-red-50 disabled:opacity-50 transition-colors"
                              title={`Undo override from ${moment(companyQuota.comprehensive.latestOverrideDate).format('MMM DD, HH:mm')}`}
                            >
                              {undoingComplete ? (
                                <MdRefresh className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <MdUndo className="h-3.5 w-3.5" />
                              )}
                              Undo Override
                            </button>
                          )}
                        </div>
                        <div className="p-6">
                          {companyQuota ? (
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-500">Cycles Consumed</p>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-3xl font-bold text-gray-900">{companyQuota.comprehensive?.used || 0}</span>
                                  <span className="text-sm text-gray-500">/ {companyQuota.comprehensive?.quota || '∞'} units</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-500">Remaining</p>
                                <p className={`text-2xl font-bold ${companyQuota.comprehensive?.remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {companyQuota.comprehensive?.remaining || 0}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="h-16 flex items-center justify-center">
                              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                            </div>
                          )}

                          {/* Coverage Progress Section */}
                          {companyQuota?.comprehensive?.currentCycle && (
                            <div className="mt-6 space-y-4 rounded-xl bg-gray-50 p-4 border border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className={`h-2 w-2 rounded-full ${companyQuota.comprehensive.currentCycle.isStarted ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    {companyQuota.comprehensive.currentCycle.isStarted ? 'Cycle In Progress' : 'No Active Cycle'}
                                  </span>
                                </div>
                                <span className="text-xs font-medium text-gray-500">
                                  {companyQuota.comprehensive.currentCycle.coveredPairs} / {companyQuota.comprehensive.currentCycle.totalPairs} pairs covered
                                </span>
                              </div>
                              
                              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                <div 
                                  className="h-full bg-blue-600 transition-all duration-1000" 
                                  style={{ width: `${(companyQuota.comprehensive.currentCycle.coveredPairs / Math.max(1, companyQuota.comprehensive.currentCycle.totalPairs)) * 100}%` }}
                                ></div>
                              </div>

                              <div className="grid grid-cols-1 gap-2">
                                {companyQuota.comprehensive.currentCycle.assetStats?.slice(0, 3).map((asset: any) => (
                                  <div key={asset.assetId} className="flex items-center justify-between text-[11px]">
                                    <span className="text-gray-600 truncate max-w-[150px]">{asset.assetName}</span>
                                    <div className="flex items-center gap-1.5">
                                      <div className="h-1 w-16 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-indigo-400" 
                                          style={{ width: `${(asset.coveredTargets / Math.max(1, asset.totalTargets)) * 100}%` }}
                                        ></div>
                                      </div>
                                      <span className={asset.coveredTargets === asset.totalTargets ? "text-green-600 font-bold" : "text-gray-400"}>
                                        {asset.coveredTargets}/{asset.totalTargets}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                                {companyQuota.comprehensive.currentCycle.assetStats?.length > 3 && (
                                  <p className="text-[10px] text-center text-gray-400 pt-1 font-medium">+{companyQuota.comprehensive.currentCycle.assetStats.length - 3} more assets being tracked</p>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-4 rounded-lg bg-amber-50 p-3 flex items-start gap-2 border border-amber-100">
                            <MdInfo className="h-4 w-4 text-amber-600 mt-0.5" />
                            <p className="text-[11px] text-amber-800 leading-relaxed">
                              A comprehensive cycle is automatically consumed when **all unique asset-target combinations** are covered. {companyQuota?.comprehensive?.remaining === 0 && <strong className="text-red-700">Quota limit reached.</strong>}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Quick Runs Card */}
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-600 shadow-md">
                              <MdPlayArrow className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">Quick / One-off Runs</h3>
                              <p className="text-xs text-gray-600">Per-run quota consumption</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-6">
                          {companyQuota ? (
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-500">Runs Used</p>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-3xl font-bold text-gray-900">{companyQuota.quick?.used || 0}</span>
                                  <span className="text-sm text-gray-500">/ {companyQuota.quick?.quota || '∞'} units</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-500">Remaining</p>
                                <p className={`text-2xl font-bold ${companyQuota.quick?.remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {companyQuota.quick?.remaining || 0}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="h-16 flex items-center justify-center">
                              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                            </div>
                          )}
                          <div className="mt-4 rounded-lg bg-blue-50 p-3 flex items-start gap-2 border border-blue-100">
                            <MdInfo className="h-4 w-4 text-blue-600 mt-0.5" />
                            <p className="text-[11px] text-blue-800 leading-relaxed">
                              Quick runs represent ad-hoc security assessments outside of seasonal compliance cycles. One run is consumed from your quota per run created.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
 

                    {/* Runs List Table */}
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="border-b border-gray-200 px-6 py-4">
                        <h3 className="text-lg font-bold text-gray-900">Run History</h3>
                      </div>
                      <div className="p-0">
                        {runsLoading ? (
                          <div className="py-12 flex justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                          </div>
                        ) : companyRuns.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Program / Title</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" style={{ width: '200px' }}>Timeline</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {companyRuns.map((run) => (
                                  <tr key={run.run_id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/dashboard/programs/${run.program_id}`)}>
                                    <td className="px-6 py-4">
                                      <div className="text-sm font-bold text-gray-900">{run.run_title}</div>
                                      <div className="text-xs text-blue-600 font-medium">{run.program?.program_title}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${
                                        run.run_type === 'comprehensive' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {run.run_type}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-xs text-gray-600">
                                      <div className="font-medium">{moment(run.start_date).format('MMM DD')} - {moment(run.end_date).format('MMM DD, YYYY')}</div>
                                      <div className="text-[10px] text-gray-400 mt-0.5">{moment(run.end_date).diff(moment(run.start_date), 'days')} days duration</div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold leading-5 ${
                                        run.status === 'completed' ? 'bg-green-100 text-green-800' :
                                        run.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                        run.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="py-20 text-center">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-50">
                              <MdTimeline className="h-8 w-8 text-gray-300" />
                            </div>
                            <h3 className="mt-4 text-sm font-semibold text-gray-900">No runs found</h3>
                            <p className="mt-1 text-sm text-gray-500">This company hasn't executed any program runs yet.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "assets" && (
                  <AssetInventoryPage companyId={company.company_id} />
                )}
      </div>

      {/* Program Quick View */}
      <ProgramQuickView
        program={selectedProgram || undefined}
        onClose={() => setSelectedProgram(undefined)}
      />

      {/* Access Password Modal for Linking */}
      {showAccessPasswordModal && selectedLinkVault && selectedLinkProgram && (
        <AccessPasswordModal
          isOpen={showAccessPasswordModal}
          onClose={() => {
            setShowAccessPasswordModal(false);
            setSelectedLinkProgram(undefined);
            setShowLinkProgramModal(true);
          }}
          onSubmit={handleAccessPasswordSubmit}
          vault={selectedLinkVault}
        />
      )}

      {/* Scoped Report Modal */}
      {showScopedReportModal && (
        <ScopedReportModal
          isOpen={showScopedReportModal}
          onClose={() => setShowScopedReportModal(false)}
          companyId={company.company_id}
          companyName={company.company_name}
        />
      )}

      {/* Manage Program Links Modal */}
      {showLinkProgramModal && selectedLinkVault && (() => {
        const linked = linkedProgramsMap[selectedLinkVault.vault_id] || [];
        const unlinked = programs.filter(p => !linked.some(lp => lp.programId === p.id));

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => {
              setShowLinkProgramModal(false);
              setSelectedLinkVault(null);
            }} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-5 bg-white border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <FiLink className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Manage Program Links</h2>
                    <p className="text-[13px] text-slate-500 font-medium truncate max-w-[250px]">{selectedLinkVault.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowLinkProgramModal(false);
                    setSelectedLinkVault(null);
                  }}
                  className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-slate-600 transition-all"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* Linked Programs Section */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Linked Programs ({linked.length})
                  </h3>
                  {linked.length > 0 ? (
                    <div className="space-y-2">
                      {linked.map((lp) => (
                        <div
                          key={lp.programId}
                          className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FiShield className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate text-sm">
                              {lp.programTitle}
                            </p>
                            <p className="text-xs text-blue-600">
                              {lp.isActivated ? "Active" : "Inactive"} • {lp.accessType}
                            </p>
                          </div>
                          <button
                            onClick={() => handleUnlinkVault(selectedLinkVault.vault_id, lp.programId)}
                            className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors shadow-sm"
                          >
                            Unlink
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic py-2">No programs currently linked to this vault.</p>
                  )}
                </div>

                {/* Available Programs Section */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Available Programs ({unlinked.length})
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Select a program to establish a secure, Zero-Knowledge link.
                  </p>

                  {unlinked.length > 0 ? (
                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                      {unlinked.map((program) => (
                        <div
                          key={program.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-xl hover:border-blue-500 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {program.profilePicture ? (
                              <img
                                src={program.profilePicture}
                                alt={program.title}
                                className="w-9 h-9 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                                <FiShield className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 truncate text-sm">
                                {program.title}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {program.isActivated ? "Active" : "Inactive"}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedLinkProgram(program);
                              setShowAccessPasswordModal(true);
                              setShowLinkProgramModal(false);
                            }}
                            className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                          >
                            Link
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic py-2">All company programs have been linked to this vault.</p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end rounded-b-2xl">
                <button
                  onClick={() => {
                    setShowLinkProgramModal(false);
                    setSelectedLinkVault(null);
                  }}
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-semibold transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CompanyDashboard;

